'use client'

import * as React from 'react'
import { Download, FileText, Search, Upload } from 'lucide-react'
import { addDoc, collection, getDocs, query, serverTimestamp, where } from 'firebase/firestore'
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage'
import { Badge } from 'components/ui/badge'
import { Button } from 'components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card'
import { Input } from 'components/ui/input'
import { toast } from 'sonner'
import { auth, db, storage } from '../../../../firebase'
import { useProject } from './project-context'
import { useUser } from 'components/context/user-context'

const AUDIT_COLLECTION = 'auditLogs'

function formatDate(value) {
  if (!value) return '—'
  if (typeof value?.toDate === 'function') {
    return value.toDate().toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '—'
  return parsed.toLocaleDateString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function formatFileSize(bytes) {
  if (!Number.isFinite(bytes)) return '—'
  if (bytes >= 1000000) return `${(bytes / 1000000).toFixed(1)} MB`
  return `${(bytes / 1000).toFixed(0)} KB`
}

function getFileType(fileName = '') {
  const ext = fileName.split('.').pop()?.toLowerCase() || ''
  if (['pdf'].includes(ext)) return 'pdf'
  if (['doc', 'docx'].includes(ext)) return 'word'
  if (['xls', 'xlsx'].includes(ext)) return 'excel'
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 'image'
  return 'file'
}

export default function ProjectDocumentsPage() {
  const { project } = useProject()
  const { canEdit } = useUser()
  const [documents, setDocuments] = React.useState([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [search, setSearch] = React.useState('')
  const [uploading, setUploading] = React.useState(false)
  const fileInputRef = React.useRef(null)

  React.useEffect(() => {
    let isMounted = true

    async function loadDocuments() {
      try {
        const docsQuery = query(collection(db, 'documents'), where('projectId', '==', project.id))
        const snapshot = await getDocs(docsQuery)
        if (!isMounted) return
        const records = snapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }))
        setDocuments(records)
      } catch {
        setDocuments([])
      } finally {
        if (isMounted) setIsLoading(false)
      }
    }

    loadDocuments()

    return () => {
      isMounted = false
    }
  }, [project.id])

  const filteredDocuments = documents.filter((doc) =>
    (doc.name || doc.fileName || '').toLowerCase().includes(search.toLowerCase()),
  )

  const handleUploadFiles = async (files) => {
    const incoming = Array.from(files || [])
    if (incoming.length === 0) return
    if (uploading) return
    setUploading(true)
    try {
      await Promise.all(
        incoming.map(async (file, index) => {
          const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_')
          const uniqueSuffix = `${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`
          const path = `project-documents/${project.id}/${uniqueSuffix}-${safeName}`
          const storageRef = ref(storage, path)
          await uploadBytes(storageRef, file, { contentType: file.type })
          const url = await getDownloadURL(storageRef)
          const payload = {
            projectId: project.id,
            name: file.name,
            type: getFileType(file.name),
            size: file.size,
            url,
            path,
            version: 1,
            uploadedByName: auth.currentUser?.displayName || auth.currentUser?.email || 'Admin',
            uploadedBy: auth.currentUser?.uid || 'system',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          }
          const docRef = await addDoc(collection(db, 'documents'), payload)
          setDocuments((prev) => [{ id: docRef.id, ...payload }, ...prev])
          try {
            await addDoc(collection(db, AUDIT_COLLECTION), {
              timestamp: serverTimestamp(),
              userId: auth.currentUser?.uid || 'system',
              userDisplayName: auth.currentUser?.displayName || auth.currentUser?.email || 'System',
              action: 'UPLOAD',
              entity: 'Project',
              entityId: project.id,
              changes: {
                before: '',
                after: `Uploaded document "${file.name}"`,
              },
            })
          } catch (error) {
            console.warn('Failed to write audit log', error)
          }
        }),
      )
      toast.success('Documents uploaded.')
    } catch (error) {
      toast.error('Failed to upload documents', {
        description: error?.message || 'Please try again.',
      })
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="text-base">Project Documents</CardTitle>
          <div className="flex items-center gap-2">
            <div className="relative w-full max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                placeholder="Search documents..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="pl-9"
              />
            </div>
            {canEdit ? (
              <>
                <Button size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                  <Upload className="size-4 mr-2" />
                  {uploading ? 'Uploading...' : 'Upload'}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(event) => {
                    handleUploadFiles(event.target.files)
                    event.target.value = ''
                  }}
                />
              </>
            ) : null}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading documents...</p>
          ) : filteredDocuments.length === 0 ? (
            <p className="text-sm text-muted-foreground">No documents uploaded yet.</p>
          ) : (
            <div className="space-y-2">
              {filteredDocuments.map((doc) => (
                <div
                  key={doc.id}
                  className="flex flex-col gap-2 rounded-lg border border-border p-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex size-8 items-center justify-center rounded-md bg-primary/10">
                      <FileText className="size-4 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {doc.name || doc.fileName || 'Document'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Uploaded {formatDate(doc.createdAt)} · {formatFileSize(doc.size)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-[10px]">
                      v{doc.version || 1}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-8"
                      aria-label="Download"
                      onClick={() => doc.url && window.open(doc.url, '_blank')}
                    >
                      <Download className="size-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
